// The UW/LM Task Shape

import { ActionableEvents } from "./actionable-events";
import { DisclosureStage } from "./disclosures";
import { DocTypes } from "./documents";
import { DisclosureType } from "./fees";
import { CashCloseTypes, CashCloseCashOfferStages, CashCloseTradeInStages } from "./index";

interface TaskBaseShape {
  id: number;
  index_order: number;
  name: string;
  owner: string;
  category: string;
  stage: TaskStage;
  include_in_queues?: boolean;
  due_date_params: TaskDueDateParams;
  completion_conditions: TaskClause;
  is_critical: boolean;
  pinned: boolean;
  blocks_loan_decision: boolean;
}

export type TaskShape = TaskBaseShape & {
  key: string;
  status: string;

  status_updated_at: string;
  status_updated_by_id: number;

  operator_override_due_date: string;
  resolved_due_date: string;
  computed_due_date: string;

  loan_info: any;
  next_task: TaskShape;
  next_task_category_completion_info: CompletionInfo;
  completion_hint: string;
};

export type ReferenceTaskShape = TaskBaseShape & {
  description: string;
  visibility_conditions: TaskClause;
  blocks_loan_decision: boolean;
};

interface CompletionInfo {
  percent_complete: string;
}
// Task Owners

export enum TaskOwner {
  LMLender = "Loan Manager - Lender",
  Underwriter = "Underwriter",
}

export const TASK_OWNER_OPTIONS = [TaskOwner.LMLender, TaskOwner.Underwriter];

// Stages
export enum TaskStage {
  // UW
  PreApplication = "Pre-Application",
  RESPA = "RESPA",

  // LM
  PreProcessing = "Pre-Processing",
  Processing = "Processing",
  Closing = "Closing",
  PostClosing = "Post-Closing",
}

export const TASK_STAGE_OPTIONS_IN_ORDER = [
  TaskStage.PreApplication,
  TaskStage.PreProcessing,
  TaskStage.Processing,
  TaskStage.RESPA,
  TaskStage.Closing,
  TaskStage.PostClosing,
];

export const TASK_STAGE_OPTIONS_BY_OWNER = {
  [TaskOwner.Underwriter]: [TaskStage.PreApplication, TaskStage.RESPA],
  [TaskOwner.LMLender]: [
    TaskStage.PreProcessing,
    TaskStage.Processing,
    TaskStage.Closing,
    TaskStage.PostClosing,
  ],
};

// Task Status

export enum TaskStatus {
  Open = "Open",
  Requested = "Requested",
  NotApplicable = "N/A",
  Cleared = "Cleared",
}

export const TASK_STATUS_OPTIONS = [TaskStatus.Open, TaskStatus.NotApplicable, TaskStatus.Cleared];

// Categories
export enum TaskCategory {
  // UW
  InitialReview = "Initial Review",
  ConditionsReview = "Conditions Review",
  FinalReview = "Final Review",

  // LM
  InitialDisclosurePackage = "Initial Disclosure Package",
  ThirdPartyOrders = "Third Party Orders",
  PropertyDocFollowUp = "Property Doc Follow Up",
  RevisedLoanEstimate = "Revised Loan Estimate",
  UWConditionsFollowUp = "UW Conditions Follow Up",
  ClosingPreparation = "Closing Preparation",
  PostClosingPreparation = "Post-Closing Preparation",
  FundingReview = "Funding Review",
  ServicingTransfer = "Servicing Transfer",
  PendingTrailingDocs = "Pending Trailing Docs",
}

export const TASK_CATEGORIES_OPTIONS = [
  TaskCategory.InitialDisclosurePackage,
  TaskCategory.ThirdPartyOrders,
  TaskCategory.PropertyDocFollowUp,
  TaskCategory.RevisedLoanEstimate,
  TaskCategory.UWConditionsFollowUp,
  TaskCategory.ClosingPreparation,
  TaskCategory.PostClosingPreparation,
  TaskCategory.FundingReview,
  TaskCategory.ServicingTransfer,
  TaskCategory.PendingTrailingDocs,
];

export const TASK_CATEGORIES_OPTIONS_BY_OWNER = {
  [TaskOwner.Underwriter]: [
    TaskCategory.InitialReview,
    TaskCategory.ConditionsReview,
    TaskCategory.FinalReview,
  ],
  [TaskOwner.LMLender]: [
    TaskCategory.InitialDisclosurePackage,
    TaskCategory.ThirdPartyOrders,
    TaskCategory.PropertyDocFollowUp,
    TaskCategory.RevisedLoanEstimate,
    TaskCategory.UWConditionsFollowUp,
    TaskCategory.ClosingPreparation,
    TaskCategory.PostClosingPreparation,
    TaskCategory.FundingReview,
    TaskCategory.ServicingTransfer,
    TaskCategory.PendingTrailingDocs,
  ],
};

// Task Options
export enum DueDateDayTypes {
  business_days = "Business Days",
  calendar_days = "Calendar Days",
  cfpb_specific_business_days = "CFPB Specific Business Days",
}

export enum DueDateOperators {
  before = "Before",
  after = "After",
}

export enum DueDateReferenceDates {
  closing = "Signing Date",
  fees_ingested = "Fees Ingested",
  investor_loan_purchase = "Investor Loan Purchase",
  rate_lock = "Rate Lock",
  respa = "RESPA",
  borrower_auth_signed = "Borrower Auth Signed",
  appraisal_received = "Appraisal Received",
  appraisal_sent_to_borrower = "Appraisal sent to borrower",
  task_creation = "Task creation",
  appraisal_ordered = "Appraisal order placed",
  appraisal_expected = "Appraisal expected date",
  appraisal_assigned = "Appraisal order assigned",
  appraisal_scheduled = "Appraisal scheduled date",
  appraisal_inspection = "Appraisal inspection scheduled date",
  now = "Now",
}

export interface TaskDueDateParams {
  days: number;
  type_of_day: DueDateDayTypes;
  before_or_after: DueDateOperators;
  reference_date: DueDateReferenceDates;
}

export enum DateOperators {
  GreaterThan = ">",
  GreaterThanOrEqual = ">=",
  LessThan = "<",
  LessThanOrEqual = "<=",
  Equal = "==",
  Null = "null",
  NotNull = "notNull",
}

export enum DecimalOperators {
  GreaterThan = ">",
  GreaterThanOrEqual = ">=",
  LessThan = "<",
  LessThanOrEqual = "<=",
  Equal = "==",
}

export enum TaskConfigFunctionName {
  // No Comparison Value Required
  isRESPA = "isRESPA",
  initialLEBeforeRateLock = "initialLEBeforeRateLock",
  isJumbo = "isJumbo",
  isVA = "isVA",
  hasGiftFunds = "hasGiftFunds",
  hasOpenLiabilities = "hasOpenLiabilities",
  hasREOs = "hasREOs",
  isCondo = "isCondo",
  borrowerAuthSigned = "borrowerAuthSigned",
  hasProfitAndLossStatementOrBalanceSheet = "hasProfitAndLossStatementOrBalanceSheet",
  hasAppraisalRequestedDate = "hasAppraisalRequestedDate",
  hasAppraisalExpectedDate = "hasAppraisalExpectedDate",
  isCondoOrPUD = "isCondoOrPUD",
  conformingOrHighBalance = "conformingOrHighBalance",
  loanProductCIC = "loanProductCIC",

  // Single Value
  applicationDateAfter = "applicationDateAfter",
  borrowerAuthAfter = "borrowerAuthAfter",
  hasDisclosure = "hasDisclosure",
  hasDocument = "hasDocument",
  hasEvent = "hasEvent",
  doesNotHaveEvent = "doesNotHaveEvent",
  loanStatusIsOrHasBeen = "loanStatusIsOrHasBeen",
  loanDecisionIsOrHasBeen = "loanDecisionIsOrHasBeen",

  // Enum comparison
  isInState = "isInState",
  isCashCloseType = "isCashCloseType",
  isTradeInInStage = "isTradeInInStage",
  isCashOfferInStage = "isCashOfferInStage",

  // Numerical comparison
  ltvComparison = "ltvComparison",
  loanAmountComparison = "loanAmountComparison",

  // Date comparison
  dateComparison = "dateComparison",
}

export type TaskConfigParamOptions = {
  function_names: TaskConfigFunctionName[];

  actionable_events: ActionableEvents[];
  date_comparison_operators: DateOperators[];
  decimal_operators: DecimalOperators[];
  disclosure_stages: DisclosureStage[];
  document_types: DocTypes[];
  task_categories: TaskCategory[];
  task_stages: TaskStage[];
  loan_statuses: string[];
  loan_decisions: string[];
  cash_close_types: CashCloseTypes[];
  cash_offer_stages: CashCloseCashOfferStages[];
  trade_in_stages: CashCloseTradeInStages[];

  due_date_day_types: DueDateDayTypes[];
  due_date_operators: DueDateOperators[];
  due_date_reference_dates: DueDateReferenceDates[];
};

export enum LogicalOperator {
  AND = "AND",
  OR = "OR",
}

export type TaskFunctionParams = {
  date?: string;
  decimal_operator: DecimalOperators;
  disclosure_type?: DisclosureType;
  document_type?: DocTypes;
  amount?: number;
  string?: string;
  options?: any;
  reference_date?: DueDateReferenceDates;
  date_comparison?: DateOperators;
  days?: number;
  type_of_day?: DueDateDayTypes;
  before_or_after?: DueDateOperators;
  other_reference_date?: DueDateReferenceDates;
};

export type TaskConditionalShape = {
  func_name: string;
  params: TaskFunctionParams;
};

export type TaskClause = {
  operator: LogicalOperator;
  conditions: TaskConditionalShape[];
};
